import CartItemModel, { CartItemData } from "../../models/CartItemModel";
import { Commit } from "vuex";
import Vue from "vue";
import OrderModel, { OrderData, create as createOrder } from "../../models/OrderModel";
import ApiService from "../../lib/api";

interface CartState {
  order: OrderModel;
  items: { [id: string]: CartItemModel };
  showCart: boolean;
}

interface Actions {
  getters: any;
  commit: Commit;
}

const state: CartState = {
  order: createOrder(),
  items: {},
  showCart: false
}

const mutations = {
  addItem(store: CartState, payload: CartItemModel): void {
    Vue.set(store.items, payload.data.sku, payload);
  },
  removeItem(store: CartState, sku: string): void {
    Vue.delete(store.items, sku);
  },
  updateItemProperty(store: CartState, payload: {sku: string, property: string, value: unknown}): void {
    Vue.set(store.items[payload.sku].data, payload.property, payload.value);
  },
  setOrder(store: CartState, payload: OrderModel): void {
    Vue.set(store, 'order', payload);
  },
  clearCart(store: CartState): void {
    Vue.set(store, 'items', {});
  },
  toggleCart(store: CartState, payload: boolean): void {
    store.showCart = payload;
  }
};

const getters = {
  allItems(state: CartState): CartItemModel[] {
    return Object.values(state.items);
  },
  bySKU: (state: CartState) => (sku: string): CartItemModel | null => {
    return state.items[sku] || null;
  },
  showCart: (state: CartState) => state.showCart,
};

const actions = {
  addItem({commit, getters}: Actions, payload: CartItemModel): Promise<void> {
    return new Promise((resolve, reject) => {
      if (getters.bySKU(payload.data.sku) === null) {
        commit('addItem', payload);
        resolve();
      } else {
        reject();
      }
    });
  },
  removeItem({commit}: Actions, sku: string): void {
    commit('removeItem', sku);
  },
  updateItemProperty({commit}: Actions, payload: {sku: string, property: string, value: unknown}): void {
    commit('updateItemProperty', payload);
  },
  verifyOrder({commit}: Actions, payload: {orderId: string, clientResponse: string}): Promise<OrderModel | any> {
    return new Promise((resolve, reject) => {
      ApiService.post('/events/orders', {external_id: payload.orderId, client_response: payload.clientResponse}).then((resp: ApiResponseSingle<OrderData>) => {
        const order = createOrder(resp.data);
        commit('setOrder', order);
        resolve(order);
      }).catch(reject);
    });
  },
  clearCart({commit}: Actions): void {
    commit('clearCart');
  },
  submitFreeOrder({commit}: Actions, payload: {items: CartItemModel[]}): Promise<OrderModel | any> {
    return new Promise((resolve, reject) => {
      const items: CartItemData[] = [];
      for (const cItem of payload.items) {
        items.push(cItem.data);
      }

      ApiService.post('/events/orders', {items}).then((resp: ApiResponseSingle<OrderData>) => {
        const order = createOrder(resp.data);
        commit('setOrder', order);
        resolve(order);
      }).catch(reject);
    });
  },
  showCart({commit}: Actions, payload: boolean) {
    commit('toggleCart', payload);
  }
};

export default {
  namespaced: true,
  mutations,
  state,
  getters,
  actions,
};
